import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ArrowBack,
    CheckCircleOutline,
    Close,
    ErrorOutline,
} from '@mui/icons-material';
import {
    useExceptionGroupLogs,
    useExceptionGroups,
    useExceptionLog,
    useResolveExceptionGroup,
} from '../../hooks/useException';
import { formatKstDateTime } from '../../utils/dateTime';

const fmtDate = (s: string) => {
    return formatKstDateTime(s);
};

const LogDetailDialog: FC<{ logId: number; onClose: () => void }> = ({ logId, onClose }) => {
    const { data, isLoading } = useExceptionLog(logId);

    return (
        <Dialog open maxWidth="md" fullWidth onClose={onClose}>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">로그 상세 #{logId}</Typography>
                    <IconButton size="small" onClick={onClose}><Close /></IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : !data ? null : (
                    <Box>
                        {/* 상단: Exception 내용 */}
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            EXCEPTION
                        </Typography>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mb: 3,
                                bgcolor: '#1a1a2e',
                                maxHeight: 320,
                                overflowY: 'auto',
                            }}
                        >
                            <Typography
                                variant="caption"
                                component="pre"
                                sx={{
                                    color: '#e57373',
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    m: 0,
                                }}
                            >
                                {data.stackTrace ?? '(스택 트레이스 없음)'}
                            </Typography>
                        </Paper>

                        {/* 하단: Payload 요약 */}
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            REQUEST 요약
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box display="grid" gridTemplateColumns="120px 1fr" gap={0.5} mb={1}>
                                <Typography variant="body2" color="text.secondary">Handler</Typography>
                                <Typography variant="body2" fontFamily="monospace">{data.handler ?? '-'}</Typography>

                                <Typography variant="body2" color="text.secondary">Method</Typography>
                                <Typography variant="body2" fontFamily="monospace">{data.method ?? '-'}</Typography>

                                <Typography variant="body2" color="text.secondary">URI</Typography>
                                <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                                    {data.uri ?? '-'}
                                </Typography>

                                <Typography variant="body2" color="text.secondary">IP</Typography>
                                <Typography variant="body2" fontFamily="monospace">{data.ip ?? '-'}</Typography>

                                <Typography variant="body2" color="text.secondary">시각</Typography>
                                <Typography variant="body2">{fmtDate(data.createdAt)}</Typography>
                            </Box>

                            {data.body && (
                                <>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                        BODY
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{ p: 1.5, bgcolor: 'background.default', maxHeight: 200, overflowY: 'auto' }}
                                    >
                                        <Typography
                                            variant="caption"
                                            component="pre"
                                            sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 0 }}
                                        >
                                            {data.body}
                                        </Typography>
                                    </Paper>
                                </>
                            )}
                        </Paper>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

const ExceptionGroupDetail: FC = () => {
    const { groupId } = useParams<{ groupId: string }>();
    const navigate = useNavigate();
    const id = Number(groupId);

    const [page, setPage] = useState(0);
    const [selectedLogId, setSelectedLogId] = useState<number | null>(null);

    // 그룹 단건 조회 API가 없으므로 전체 목록에서 검색
    const { data: logsData, isLoading } = useExceptionGroupLogs(id, page);
    const resolveGroup = useResolveExceptionGroup();

    // 그룹 상세 정보를 위해 별도 조회 (첫 페이지 모든 상태에서 찾기)
    const { data: allGroups } = useExceptionGroups(0, 200, undefined);
    const group = allGroups?.content.find((g) => g.id === id);

    return (
        <Box p={3}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Tooltip title="목록으로">
                    <IconButton onClick={() => navigate('/exceptions')} size="small">
                        <ArrowBack />
                    </IconButton>
                </Tooltip>
                <Typography variant="h5" fontWeight="bold">예외 로그 상세</Typography>
            </Box>

            {group && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography variant="subtitle1" fontFamily="monospace" fontWeight="bold" color="error.main">
                                {group.exceptionType}
                            </Typography>
                            {group.exceptionMessage && (
                                <Typography variant="body2" color="text.secondary" mt={0.5}>
                                    {group.exceptionMessage}
                                </Typography>
                            )}
                            <Box display="flex" gap={2} mt={1}>
                                <Typography variant="caption" color="text.secondary">
                                    최초: {fmtDate(group.firstOccurrenceAt)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    최근: {fmtDate(group.lastOccurrenceAt)}
                                </Typography>
                                <Chip
                                    label={`${group.occurrenceCount}회 발생`}
                                    size="small"
                                    color={group.occurrenceCount >= 10 ? 'error' : 'warning'}
                                />
                                <Chip
                                    label={group.isResolved ? '해결됨' : '미해결'}
                                    size="small"
                                    color={group.isResolved ? 'success' : 'error'}
                                    variant={group.isResolved ? 'outlined' : 'filled'}
                                />
                            </Box>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            color={group.isResolved ? 'warning' : 'success'}
                            startIcon={group.isResolved ? <ErrorOutline /> : <CheckCircleOutline />}
                            onClick={() =>
                                resolveGroup.mutate({ groupId: id, isResolved: !group.isResolved })
                            }
                            disabled={resolveGroup.isPending}
                        >
                            {group.isResolved ? '미해결로 변경' : '해결됨으로 표시'}
                        </Button>
                    </Box>
                </Paper>
            )}

            <Typography variant="subtitle2" color="text.secondary" mb={1}>발생 로그</Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Method</TableCell>
                            <TableCell>URI</TableCell>
                            <TableCell>IP</TableCell>
                            <TableCell>Handler</TableCell>
                            <TableCell>발생 시각</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">로딩 중...</TableCell>
                            </TableRow>
                        ) : (logsData?.content ?? []).length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="text.secondary" py={2}>
                                        로그가 없습니다
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            (logsData?.content ?? []).map((log) => (
                                <TableRow
                                    key={log.id}
                                    hover
                                    onClick={() => setSelectedLogId(log.id)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>
                                        <Typography variant="caption" fontFamily="monospace">#{log.id}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={log.method ?? '-'} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 300 }}>
                                        <Typography
                                            variant="caption"
                                            fontFamily="monospace"
                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                                        >
                                            {log.uri ?? '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">{log.ip ?? '-'}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                        <Typography
                                            variant="caption"
                                            fontFamily="monospace"
                                            color="text.secondary"
                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                                        >
                                            {log.handler ?? '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {fmtDate(log.createdAt)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={logsData?.totalCount ?? 0}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={30}
                rowsPerPageOptions={[30]}
                labelRowsPerPage="페이지당:"
            />

            {selectedLogId !== null && (
                <LogDetailDialog
                    logId={selectedLogId}
                    onClose={() => setSelectedLogId(null)}
                />
            )}
        </Box>
    );
};

export default ExceptionGroupDetail;
